import { ConfigService, LaunchDarklyFlag, LaunchDarklyService } from '@aiwyn/config';
import { DATA_SELECTOR_INJECTION_TOKEN, DialogSize, Selectable, SnackbarService, SnackbarType } from '@aiwyn/shared';
import { AfterViewInit, Component, Inject, inject, OnDestroy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngxs/store';
import { BehaviorSubject, combineLatest, defer, Observable, of, Subject } from 'rxjs';


import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import {
  ClientRequestDocumentFileCategorize,
  ClientRequestItemStatus,
  ItemSubmissionFile,
  LinkFileToDocumentsRequestsPayload,
  RequestDocument,
  RequestItemType,
} from '@aiwyn/api';
import { InsertCategorizedFilesIntoClientRequest, UnCategorizeFilesFromClientRequestDocuments } from '@aiwyn/common/client-requests';
import { map, shareReplay, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { ClientRequestLinkFilesToDocuments } from '../../state';
import { FilesLinkModalData, RequestSectionWithSelectableDocuments, SelectableDocument } from './files-link-modal.model';

const STATUS_TO_TEXT = {
  [ClientRequestItemStatus.DONE]: 'Done',
  [ClientRequestItemStatus.IN_REVIEW]: 'In Review',
};

@Component({
  selector: 'app-files-link-modal',
  templateUrl: './files-link-modal.component.html',
  styleUrl: './files-link-modal.component.scss',
  providers: [
    {
      provide: DATA_SELECTOR_INJECTION_TOKEN,
      useValue: 'files-link-modal',
    },
  ],
  standalone: false,
})
export class FilesLinkModalComponent implements AfterViewInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  protected readonly DialogSize = DialogSize;
  public dataSelector = inject(DATA_SELECTOR_INJECTION_TOKEN);

  // LaunchDarkly flag for IN_REVIEW items editing
  private isInReviewEditingEnabled = toSignal(inject(LaunchDarklyService).getFeatureFlag(LaunchDarklyFlag.ENABLE_IN_REVIEW_ITEMS_EDITING), {
    initialValue: false,
  });

  acceptedFileTypes: string[] = [];

  selectedDocumentsId: number[] = [];
  selectedDocumentsId$: BehaviorSubject<number[]> = new BehaviorSubject<number[]>([]);

  isLoading: boolean = false;

  rawSections$ = defer(() => this.data.instance.typeClass$).pipe(
    switchMap((typeClass) => typeClass.sections$),
    shareReplay(1),
    takeUntil(this.destroy$),
  );

  sections$: Observable<RequestSectionWithSelectableDocuments[]> = combineLatest([this.rawSections$, this.selectedDocumentsId$]).pipe(
    map(([sections, selectedDocumentsId]) => {
      return sections.map((section) => {
        return {
          ...section,
          documents: section.items
            .filter((item) => item.type === RequestItemType.DOCUMENT_REQUEST)
            .map((document) => {
              const isSelected = selectedDocumentsId.some((id) => id === document.id);
              const isDisabled =
                document.status === ClientRequestItemStatus.DONE ||
                document.status === ClientRequestItemStatus.IN_REVIEW ||
                this.data.notApplicableItemIds?.has(document.id);
              let disabledTooltipText = '';
              if (document.status === ClientRequestItemStatus.DONE) {
                disabledTooltipText = 'Document is completed';
              } else if (document.status === ClientRequestItemStatus.IN_REVIEW) {
                disabledTooltipText = 'Document is under review';
              } else if (this.data.notApplicableItemIds?.has(document.id)) {
                disabledTooltipText = 'Document marked as not applicable';
              }

              return {
                selected: isSelected,
                disabled: isDisabled,
                disabledTooltipText: disabledTooltipText,
                data: document,
              } satisfies SelectableDocument;
            }),
        } as RequestSectionWithSelectableDocuments;
      });
    }),
  );

  hasChanges$: Observable<boolean> = combineLatest([this.rawSections$, this.selectedDocumentsId$]).pipe(
    map(([sections, currentlySelectedFilesId]) => {
      const documentRequests = sections.flatMap((section) => section.items);

      const originallySelectedDocumentsId = documentRequests
        .filter((document) => document.files.some((file) => file.file.id === this.data.file.id))
        .map((document) => document.id);

      if (originallySelectedDocumentsId.length !== currentlySelectedFilesId.length) {
        return true;
      }

      return !originallySelectedDocumentsId.every((docId) => currentlySelectedFilesId.includes(docId));
    }),
    takeUntil(this.destroy$),
  );

  constructor(
    private readonly store: Store,
    private readonly configService: ConfigService,
    private readonly snackbarService: SnackbarService,
    private dialogRef: MatDialogRef<FilesLinkModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: FilesLinkModalData,
  ) {
    this.acceptedFileTypes = this.configService.clientRequestFileTypeWhitelist;
  }

  ngAfterViewInit(): void {
    this.rawSections$
      .pipe(
        tap((sections) => {
          const documentRequests = sections.flatMap((section) => section.items);

          this.selectedDocumentsId = documentRequests
            .filter((document) => document.files.some((file) => file.file.id === this.data.file.id))
            .map((document) => document.id);

          this.selectedDocumentsId$.next(this.selectedDocumentsId);
        }),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  clearSelection(): void {
    this.sections$
      .pipe(
        take(1),
        tap((sections) => {
          const allDocuments = sections.flatMap((section) => section.documents);

          // Remove items from selection unless they are disabled
          this.selectedDocumentsId = this.selectedDocumentsId.filter((docId) => {
            const document = allDocuments.find((doc) => doc.data.id === docId);
            return document?.disabled ?? false;
          });

          this.selectedDocumentsId$.next(this.selectedDocumentsId);
        }),
      )
      .subscribe();
  }

  onClose(): void {
    this.isLoading = false;
    this.dialogRef.close(false);
  }

  selectDocument(document: Selectable<RequestDocument>, e?: MouseEvent): void {
    e?.stopPropagation();

    const isInReviewEditingEnabled = this.isInReviewEditingEnabled();

    // Check if document is DONE or (IN_REVIEW when feature flag is disabled)
    if(document.data.status === ClientRequestItemStatus.DONE ||
       (!isInReviewEditingEnabled && document.data.status === ClientRequestItemStatus.IN_REVIEW)) {
      this.snackbarService.open(`You cannot change a ${STATUS_TO_TEXT[document.data.status]} Request Item.`, SnackbarType.ERROR);
      return;
    }

    if (this.data.notApplicableItemIds?.has(document.data.id)) {
      this.snackbarService.open('You cannot change a not applicable Request Item.', SnackbarType.ERROR);
      return;
    }

    if (document.selected) {
      this.selectedDocumentsId = this.selectedDocumentsId.filter((selectedDocumentId) => selectedDocumentId !== document.data.id);
    } else {
      this.selectedDocumentsId.push(document.data.id);
    }

    this.selectedDocumentsId$.next(this.selectedDocumentsId);
  }

  updateLinks(): void {
    this.isLoading = true;

    const linkFileToDocumentsRequestsPayload: LinkFileToDocumentsRequestsPayload = {
      clientRequestId: this.data.clientRequestId,
      fileId: this.data.file.id,
      clientRequestItemIds: this.selectedDocumentsId,
    };

    this.store
      .dispatch(new ClientRequestLinkFilesToDocuments(linkFileToDocumentsRequestsPayload))
      .pipe(
        switchMap(() => combineLatest([this.sections$, this.rawSections$])),
        take(1),
        switchMap(([sections, rawSections]) => {
          // #region Previously SelectedFiles
          const documentRequests = rawSections.flatMap((section) => section.items);
          const previouslySelectedDocuments = documentRequests
            .filter((document) => document.files.some((file) => file.file.id === this.data.file.id))
            .map((document) => document.id);
          // #endregion

          const selectedDocuments: RequestDocument[] = sections.flatMap((section) =>
            section.documents
              .filter(
                (document) =>
                  document.selected && !previouslySelectedDocuments.some((prevDocumentId) => prevDocumentId === document.data.id),
              )
              .map((document) => document.data),
          );
          const deselectedDocuments: RequestDocument[] = sections.flatMap((section) =>
            section.documents
              .filter(
                (document) =>
                  !document.selected && previouslySelectedDocuments.some((prevDocumentId) => prevDocumentId === document.data.id),
              )
              .map((document) => document.data),
          );

          const itemSubmissionFiles: ItemSubmissionFile[] = selectedDocuments.map((document) => {
            return {
              documentId: document.id,
              fileId: this.data.file.id,
            } as ItemSubmissionFile;
          });

          const filesToUnCategorize: Record<string, number[]> = deselectedDocuments
            .map((document) => {
              return {
                fileId: this.data.file.id,
                documentId: document.id,
              } as ClientRequestDocumentFileCategorize;
            })
            .reduce(
              (acc, { documentId, fileId }) => ({ ...acc, [documentId]: (acc[documentId] ?? []).concat(fileId) }),
              {} as Record<string, number[]>,
            );

          return combineLatest([
            deselectedDocuments.length
              ? this.store.dispatch(new UnCategorizeFilesFromClientRequestDocuments(this.data.clientRequestId, filesToUnCategorize))
              : of(undefined),

            selectedDocuments.length
              ? this.store.dispatch(new InsertCategorizedFilesIntoClientRequest(this.data.clientRequestId, itemSubmissionFiles))
              : of(undefined),
          ]);
        }),
        tap(() => {
          this.onClose();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected readonly ClientRequestItemStatus = ClientRequestItemStatus;
}
